import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useQuery } from '@apollo/client'
import styles from './childProfile.module.scss'
import Loading from '@/components/Loading/Loading'
import Title from '@/components/_custom/Title/title'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Role } from '@/types/Role'
import UserInput from '@/components/_custom/UserInput/userInput'
import moment, { Moment } from 'moment-timezone'
import { ChildDaycareWithParents } from '@/types/Child'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import { GET_CHILD } from '@/modules/API/parent/parentHandler'
import DaycareChildProfile from './daycareChildProfile/daycareChildProfile'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { GET_SECTIONS } from '@/modules/API/daycare/sectionHandler'
import { Section } from '@/types/Section'
import { KeyboardArrowDown, KeyboardArrowUp, Merge, Search } from '@mui/icons-material'
import MergeTwoChildsDialog from './Dialog/mergeTwoChildsDialog'
import SearchChildsDialog from './Dialog/searchChildsDialog.tsx'
import ConfirmDeleteChildDialog from '../parentProfile/childTable/Dialog/confirmDeleteChildDialog.tsx'
import UpdateMockChildDialog from './Dialog/updateMockChildDialog.tsx'

interface ChildProfileProps {
    childId: number
    showRegular: boolean
}

const ChildProfile: FunctionComponent<ChildProfileProps> = ({ childId, showRegular }: ChildProfileProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [showPersonalInformation, setShowPersonalInformation] = useState<boolean>(!showRegular)

    const [created, setCreated] = useState<string>('')

    const [firstname, setFirstname] = useState<string>('')
    const [birthdate, setBirthdate] = useState<string>('')
    const [preRegistrationBelgium, setPreRegistrationBelgium] = useState<string>('')

    const [daycareChild, setDaycareChild] = useState<ChildDaycareWithParents[]>([])
    const [selectedDaycareChild, setSelectedDaycareChild] = useState<ChildDaycareWithParents | null>(null)

    const [sections, setSections] = useState<SelectOptionsDeprecated>([])

    const [selectOptionsDaycare, setSelectOptionsDaycare] = useState<SelectOptionsDeprecated>([])

    const [showMergeChildDialogOpen, setMergeChildDialogOpen] = useState<boolean>(false)
    const [showSearchChildDialogOpen, setSearchChildDialogOpen] = useState<boolean>(false)
    const [showDeleteChildDialogOpen, setDeleteChildDialogOpen] = useState<boolean>(false)
    const [showUpdateChildDialogOpen, setUpdateChildDialogOpen] = useState<boolean>(false)

    function handleGoBack(): void {
        navigate(-1)
    }

    const {
        data: dataChild,
        loading: loadingChild,
        error: errorChild,
        refetch: refetchChild
    } = useQuery(GET_CHILD, {
        variables: {
            childId: childId
        }
    })

    useEffect(() => {
        if (dataChild) {
            setFirstname(dataChild.getChild.firstname)
            setBirthdate(dataChild.getChild.birthdate)
            setPreRegistrationBelgium(dataChild.getChild.preRegistrationBelgium)
            setDaycareChild(dataChild.getChild.daycares)
            if (dataChild.getChild.daycares && dataChild.getChild.daycares.length > 0) {
                const allDaycares: ChildDaycareWithParents[] = dataChild.getChild.daycares

                setSelectOptionsDaycare([
                    {
                        options: allDaycares.map((daycare: ChildDaycareWithParents): SelectOptionDeprecated => {
                            return {
                                name: daycare.daycareCity + ' - ' + daycare.daycareName,
                                value: daycare.daycareId.toString()
                            }
                        })
                    }
                ])

                const daycare: ChildDaycareWithParents | undefined = allDaycares.find(
                    (daycare: ChildDaycareWithParents): boolean => daycare.daycareId === user?.currentDaycare?.id
                )
                setSelectedDaycareChild(daycare ? daycare : allDaycares[0])
            }
            if (user?.role === Role.cald) {
                setCreated(moment(dataChild.getChild.created, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY'))
            }
        }
    }, [dataChild])

    useEffect(() => {
        if (errorChild) {
            navigate('/childs')
        }
    }, [errorChild])

    async function handleRefetchChild(): Promise<void> {
        const { data } = await refetchChild()
        if (data) {
            setFirstname(data.getChild.firstname)
            setBirthdate(data.getChild.birthdate)
            setPreRegistrationBelgium(data.getChild.preRegistrationBelgium)
            if (data.getChild.daycares && data.getChild.daycares.length > 0) {
                const allDaycares: ChildDaycareWithParents[] = data.getChild.daycares

                setDaycareChild(allDaycares)
                setSelectOptionsDaycare([
                    {
                        options: allDaycares.map((daycare: ChildDaycareWithParents): SelectOptionDeprecated => {
                            return {
                                name: daycare.daycareCity + ' - ' + daycare.daycareName,
                                value: daycare.daycareId.toString()
                            }
                        })
                    }
                ])
                const tempSelectedDaycare: number | null = selectedDaycareChild ? selectedDaycareChild.daycareId : null
                setSelectedDaycareChild(null)
                const daycare: ChildDaycareWithParents | undefined = allDaycares.find(
                    (daycare: ChildDaycareWithParents): boolean =>
                        tempSelectedDaycare
                            ? daycare.daycareId === tempSelectedDaycare
                            : daycare.daycareId === user?.currentDaycare?.id
                )
                setSelectedDaycareChild(daycare ? daycare : allDaycares[0])
            }
            if (user?.role === Role.cald) {
                setCreated(moment(data.getChild.created, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY'))
            }
        }
    }

    function handleSetSelectedDaycareChild(id: string): void {
        const daycare: ChildDaycareWithParents | undefined = daycareChild.find(
            (daycare: ChildDaycareWithParents): boolean => daycare.daycareId.toString() === id
        )
        if (daycare) setSelectedDaycareChild(daycare)
    }

    useEffect(() => {
        if (selectedDaycareChild) {
            getSections()
        }
    }, [selectedDaycareChild])

    const [getSections, { loading: loadingSections, data: dataSections }] = useLazyQuery(GET_SECTIONS, {
        variables: {
            daycare_id: selectedDaycareChild?.daycareId
        }
    })

    useEffect(() => {
        if (dataSections) {
            setSections([
                {
                    options: [
                        ...dataSections.getSections.map((section: Section): SelectOptionDeprecated => {
                            return {
                                value: section.id.toString(),
                                name: section.name
                            }
                        })
                    ]
                }
            ])
        }
    }, [dataSections])

    function computeAge(birthdate: string): string {
        const birth: Moment = moment(birthdate, 'DD-MM-YYYY')
        if (!birth.isValid()) return ''
        const totalMonths: number = moment().diff(birth, 'months')
        const years: number = Math.floor(totalMonths / 12)
        const months: number = totalMonths % 12
        if (years === 0) return `${months} mois`
        if (months === 0) return `${years} an${years > 1 ? 's' : ''}`
        return `${years} an${years > 1 ? 's' : ''} ${months} mois`
    }

    const canUpdate: boolean = !!user?.currentDaycareRights.canWriteFamilies

    return (
        <>
            {(loadingChild || loadingSections) && <Loading overall />}
            <div className={styles.PagePosition}>
                {user?.role === Role.cald &&
                    daycareChild.length <= 1 &&
                    (daycareChild.length === 0 ? (
                        <div className={styles.NoDaycareCALD}>
                            <ErrorMessage error={t('noDaycare')} className={styles.Error} />
                        </div>
                    ) : (
                        selectedDaycareChild &&
                        user.currentDaycare &&
                        selectedDaycareChild.daycareId !== user.currentDaycare.id && (
                            <div className={styles.NoDaycareCALD}>
                                <ErrorMessage
                                    error={selectedDaycareChild.daycareId + ' - ' + selectedDaycareChild.daycareName}
                                    className={styles.Info}
                                />
                            </div>
                        )
                    ))}
                <div className={styles.ProfilPlacement}>
                    {dataChild && (
                        <div className={styles.Form}>
                            <div className={styles.TitleAndButton}>
                                <div className={styles.Title}>
                                    <Title text={t('personalInformation')} />
                                    {showPersonalInformation ? (
                                        <KeyboardArrowUp
                                            onClick={() => setShowPersonalInformation(false)}
                                            className={styles.Arrow}
                                        />
                                    ) : (
                                        <KeyboardArrowDown
                                            onClick={() => setShowPersonalInformation(true)}
                                            className={styles.Arrow}
                                        />
                                    )}
                                </div>
                            </div>
                            {showPersonalInformation && (
                                <>
                                    <div className={styles.InputContainer}>
                                        {user?.role === Role.cald && (
                                            <div className={styles.Input}>
                                                <UserInput
                                                    label={t('userId')}
                                                    value={childId.toString()}
                                                    readOnly
                                                    className={styles.UserInput}
                                                />
                                                <UserInput
                                                    label={t('creationDate')}
                                                    value={created}
                                                    readOnly
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.Input}>
                                            <UserInput
                                                label={t('firstname')}
                                                value={firstname}
                                                setValue={setFirstname}
                                                readOnly
                                                className={styles.UserInput}
                                            />
                                            <UserInput
                                                label={t('birthdate')}
                                                value={birthdate}
                                                setValue={setBirthdate}
                                                readOnly
                                                className={styles.UserInput}
                                                endAdornment={birthdate ? computeAge(birthdate) : undefined}
                                            />
                                        </div>
                                        {preRegistrationBelgium && preRegistrationBelgium !== '' && (
                                            <div className={styles.Input}>
                                                <UserInput
                                                    label={t('preRegistration')}
                                                    value={preRegistrationBelgium}
                                                    setValue={setPreRegistrationBelgium}
                                                    readOnly
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {canUpdate && selectedDaycareChild?.parents.length === 0 && (
                                        <div className={styles.ButtonContainer}>
                                            <ButtonTextIcon
                                                type={'edit'}
                                                onClick={() => setUpdateChildDialogOpen(true)}
                                                className={styles.Button}
                                            />
                                            <ButtonTextIcon
                                                type={'delete'}
                                                onClick={() => setDeleteChildDialogOpen(true)}
                                                className={styles.Button}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.ProfilPlacement}>
                    {errorChild && <ErrorMessage error={t('generalError')} className={styles.Error} />}
                    {daycareChild.length > 0 && selectedDaycareChild && (
                        <DaycareChildProfile
                            daycareChild={selectedDaycareChild}
                            canUpdate={canUpdate}
                            selectOptionsSection={sections}
                            setSelectedDaycareChild={handleSetSelectedDaycareChild}
                            selectOptionsDaycare={selectOptionsDaycare}
                            childId={childId}
                            handleRefetchChild={handleRefetchChild}
                            showRegular={showRegular}
                            childName={firstname}
                        />
                    )}
                </div>
                <div className={styles.ButtonsPlacement}>
                    <ButtonTextIcon type={'return'} onClick={handleGoBack} className={styles.Button} />
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Search />}
                        customText={t('searchChild')}
                        onClick={() => setSearchChildDialogOpen(true)}
                        className={styles.Button}
                    />
                    {user?.currentDaycareRights.canWriteFamilies && daycareChild.length >= 1 && (
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('merge')}
                            customIcon={<Merge />}
                            className={styles.Button}
                            onClick={() => setMergeChildDialogOpen(true)}
                        />
                    )}
                </div>
            </div>
            {selectedDaycareChild && showMergeChildDialogOpen && (
                <MergeTwoChildsDialog
                    open={showMergeChildDialogOpen}
                    handleClose={() => setMergeChildDialogOpen(false)}
                    childId={childId}
                    childName={firstname}
                    daycareId={selectedDaycareChild?.daycareId}
                    handleRefetchChild={handleRefetchChild}
                />
            )}
            {selectedDaycareChild && showSearchChildDialogOpen && (
                <SearchChildsDialog
                    open={showSearchChildDialogOpen}
                    handleClose={() => setSearchChildDialogOpen(false)}
                    daycareId={selectedDaycareChild.daycareId}
                    childId={childId.toString()}
                />
            )}
            {selectedDaycareChild && showDeleteChildDialogOpen && (
                <ConfirmDeleteChildDialog
                    daycareId={selectedDaycareChild.daycareId}
                    deleteChild={(_c: number): void => handleGoBack()}
                    childId={childId}
                    childFirstname={firstname}
                    open={showDeleteChildDialogOpen}
                    handleClose={(): void => setDeleteChildDialogOpen(false)}
                />
            )}
            {selectedDaycareChild && showUpdateChildDialogOpen && (
                <UpdateMockChildDialog
                    daycareId={selectedDaycareChild.daycareId}
                    childId={childId}
                    childFirstname={firstname}
                    childBirthdate={birthdate}
                    childPreRegistrationBelgium={preRegistrationBelgium}
                    open={showUpdateChildDialogOpen}
                    handleClose={(): void => setUpdateChildDialogOpen(false)}
                    handleRefetchChild={handleRefetchChild}
                />
            )}
        </>
    )
}

export default ChildProfile
